// © Microsoft Corporation. All rights reserved.

import { isSapphire } from "./SapphireCore";
import { navigateAsync, getUserInfoAsync, requestCustomAsync, navigateSync, requestSnackBarAsync, subscribe, unsubscribe, sendBroadcastAsync, logEventAsync } from "./SapphireBridge";
import { getUserConfig, getAccessToken } from "./SapphireUserConfig";

const partnerId = "Home";

/**
 * Facilities the in-app navigation using navigate api
 * @param {string} url - the URL
 * @param {string} contentId - the content Id
 * @param {string} deepLink - the deep link
 * @param {string} commentId - the comment id
 * @param {string} shouldScrollToComments - the flag for comment scroll
 */
export function inAppNavigation(url: string, contentId: string, deepLink: string, commentId?: string, shouldScrollToComments?: string): void {
    const data = {
        url: url,
        deeplink: deepLink,
        articleId: contentId,
        action: "requestNativePage"
    };

    if (commentId) {
        data["commentId"] = commentId;
        data["shouldScrollToComments"] = shouldScrollToComments;
    }

    navigatePage(data);
}

/**
 * Facilities the in-app navigation using navigate api for video L2 experiences
 * @param {string} url - the URL
 * @param {string} contentId - the content Id
 * @param {string} deepLink - the deep link
 * @param {any} firstCardObject - the first card object
 */
export function inAppNavigationForVideo(url: string, contentId: string, deepLink: string, firstCardObject: any): void {
    const data: any = {
        url: url,
        firstCard: firstCardObject,
        deeplink: deepLink,
        articleId: contentId,
        action: "requestNativePage"
    };
    navigatePage(data);
}

/**
 * open the action menu
 * @param {object} menuItems - the list of action menus
 * @param {Function} callback
 */
export function openActionMenu(menuItems: any, callback: Function): void {
    const data: any = {
        action: "requestActionMenu",
        items: menuItems
    };
    navigateSync(data, callback);
}

/**
 * Get the system share panel to share content
 * @param {string} - contentToShare
 * @param {string} - titleToShare
 * @param {string} - imageToShare
 */
export function getSharePanel(contentToShare: string, titleToShare: string = undefined, imageToShare: string = undefined): void {
    const data: any = {
        action: "requestShare",
        title: titleToShare,
        content: contentToShare,
        image: imageToShare
    };
    navigatePage(data);
}

/**
 * Ability to add stories in MySaves
 * @param {string} title of the content
 * @param {string} unique key for the content
 * @param {string} url
 * @param {number} timestamp in milliseconds
 * @param {string} targetId content id
 * @param {string} type content type
 * @param {string} provider provider name
 * @param {string} image content image
 * @param {number} imageHeight content image height
 * @param {number} imageWidth content image width
 * @param {string} icon provider image
 * @param {any} firstCardObject first content for video content type
 */
export async function addMySaves(title: string, key: string, url: string, timestamp: number, targetId: string, type: string, provider: string, image: string, imageHeight: number, imageWidth: number, icon: string, firstCardObject: any): Promise<string> {
    const metaData: any = {
        partner: "MySaves",
        scenario: "Add",
        category: "News",
        data: {
            // required params
            title: title,
            key: key,
            newsId: targetId,
            // params required to open articles in L2 experiences
            type: type,
            url: url,
            timestamp: timestamp,
            image: image,
            icon: icon,
            imageHeight: imageHeight,
            imageWidth: imageWidth,
            provider: provider,
            // required video param to open video in video feed
            firstCard: firstCardObject
        }
    };
    const result = await requestCustomAsync(metaData);
    return result;
}

/**
 * Ability to delete stories from MySaves
 * @param {string} key
 */
export async function deleteMySaves(key: string) {
    const metaData: any = {
        partner: "MySaves",
        scenario: "Delete",
        category: "News",
        filters: {
            conditions: [{
                key: "key",
                value: key,
                operator: "="
            }]
        }
    };
    const result = await requestCustomAsync(metaData);
    return result;
}

/**
 * Ability to check if an article is saved or not
 * @param {string} key
 */
export async function checkMySaves(key: string) {
    const metaData: any = {
        partner: "MySaves",
        scenario: "Get",
        category: "News",
        filters: {
            conditions: [{
                key: "key",
                value: key,
                operator: "="
            }]
        }
    };
    const result = await requestCustomAsync(metaData);
    try {
        const value = JSON.parse(result);
        return (value?.success === true && !!value.result?.length);
    } catch (exception) {
        // ignore error, treat it as unsaved.
    }
    return false;
}

/**
 * Display the toast message
 * @param message
 * @param action
 * @param bgColor
 * @param period
 * @param textColor
 */
export async function displayToastMessage(message: string, bgColor: string,
    textColor: string, actionColor?: string, action = undefined, period = "long"): Promise<string> {
    const data: any = {
        message: message,
        action: action,
        period: period,
        bgColor: bgColor,
        textColor: textColor,
        actionColor: actionColor
    };
    const result = await requestSnackBarAsync(data);
    return result;
}

/**
 * Facilitates the in-app navigation using navigate api with deep link.
 * @param {string} deepLink - deep link to navigate to
 * @param {string} action - optional action. Defaults to "requestNativePage"
 */
export function inAppNavigationWithDeepLink(deepLink: string, action?: string) {
    const data = {
        deeplink: deepLink,
        action: action || "requestNativePage"
    };
    navigatePage(data);
}

/**
 * Open mini app via bridge message
 * @param targetAppId : mini app id to open
 */
export function openMiniApp(targetAppId: string, pageType?: string): void {
    const data: any = {
        action: "requestMiniApp",
        targetAppId: targetAppId,
        page: pageType
    };
    navigatePage(data);
}

/**
 * Open specific page in mini app via bridge message
 * @param targetAppId : mini app id to open
 */
export function requestMiniAppPage(targetAppId: string, pageType?: string): void {
    const data: any = {
        action: "requestMiniAppPage",
        targetAppId: targetAppId,
        page: pageType
    };
    navigatePage(data);
}

async function navigatePage(data: object): Promise<void> {
    const result = await navigateAsync(data);
    console.log(result);
}

/**
 * get the sign in data via bridge message
 * @param accountType - account type "MSA" / "AAD" / ""
 * @param scope - scope for MSA
 * @param refresh - refresh access token for MSA
 */
export async function getSignInStatus(accountType = "", scope = "", refresh = false): Promise<any> {

    const data = {
        accountType: accountType,
        scope: scope,
        refresh: refresh
    };

    const signInData = await getUserInfoAsync(data);

    return signInData;
}

/**
 * Calls bridge method to send signal to native app if homepage feed refresh is completed
 */
export function homepageFeedRefreshCompleted(): void {
    const metadata: any = {
        partner: partnerId,
        data: {
            method: "homepageFeedRefreshCompleted",
            params: {}
        }
    };

    requestCustomAsync(metadata);
}

/**
 * Calls bridge method to open artcle feedback dialog
 */
export function requestArticleFeedback(): void {
    const metadata: any = {
        partner: partnerId,
        data: {
            method: "requestArticleFeedback",
            params: {}
        }
    };

    requestCustomAsync(metadata);
}

/**
 * Calls bridge method to open Super App.
 */
export function requestSuperApp(): void {
    const metadata: any = {
        partner: partnerId,
        data: {
            method: "requestSuperApp",
            params: {}
        }
    };

    requestCustomAsync(metadata);
}

/**
 * Navigate to an URL in the SuperApp with the in app browser
 * @param {string} url - url to browse to
 */
export function navigateToUrl(url: string): void {
    const data = {
        url: url,
        action: "requestBrowser"
    };
    navigatePage(data);
}

/**
 * Returns the metadata for getPrefetchData bridge call.
 * @param url - The URL
 */
export function getPrefetchDataMetadata(url: string): any {
    const metadata: any = {
        partner: partnerId,
        data: {
            method: "getPrefetchData",
            params: {
                url: url
            }
        }
    };

    return metadata;
}

/**
 * Calls bridge method to register URL for prefetching data for.
 * @param url - The URL
 */
export function registerPrefetchUrl(url: string): void {
    const metadata: any = {
        partner: partnerId,
        data: {
            method: "addPrefetchUrls",
            params: {
                urls: [url]
            }
        }
    };

    requestCustomAsync(metadata);
}

/**
 * Returns metadata for getHttp bridge call.
 * @param url - The URL
 * @param refresh - When true, getHttp by pass the cache and makes network call, otherwise cache is used when present
 */
export function getHttpMetadata(url: string, refresh = false, headers = null): any {
    const metadata: any = {
        url: url,
        refresh: refresh
    };

    if (headers) {
        metadata["headers"] = headers;
    }

    return metadata;
}

/**
 * Returns headers with MUID and authorization token.
 */
export async function getOneServiceHeaders(): Promise<any> {
    const headers = {};

    const userConfig = getUserConfig();
    if (userConfig) {
        headers["MUID"] = userConfig.muid;

        const accessToken = await getAccessToken("service::api.msn.com::MBI_SSL");

        if (userConfig.accountType === "MSA" && accessToken) {
            headers["UserAuthToken"] = accessToken;
        } else if (userConfig.accountType === "AAD" && accessToken) {
            headers["Authorization"] = accessToken;
        }
    }

    return headers;
}

/**
 * Subscribe homepage feed refresh
 */
export function subscribeHomepageFeedRefresh(callback: any) {
    subscribe({ key: "homepageFeedRefresh" }, callback);
}

/**
 * unsubscribe homepage feed refresh
 */
export function unsubscribeHomepageFeedRefresh() {
    unsubscribe("homepageFeedRefresh", {});
}

/**
 * Logs required performance markers in super-app.
 *
 * @param {string} appPerfName
 * @param {boolean} isFinished
 */
export function logPerfMarkers(appPerfName: string, isFinished = false): void {
    if (!isSapphire()) {
        return;
    }

    sendBroadcastAsync({
        key: "StartupPerfNotify",
        intent: {
            perfName: "ScaffoldingStartUp",
            stateName: appPerfName,
            ts: (new Date()).getTime(),
            isFinish: isFinished
        }
    });

    logEventAsync({
        type: "Debug",
        name: "HP_WEBVIEW_SERVER_LOG",
        data: {
            stateName: appPerfName,
            ts: (new Date()).getTime()
        }
    });
}

/**
 * Logs one service perf
 *
 * @param {string} name
 * @param {number} startTime
 * @param {number} endTime
 * @param {string} url
 */
export function logOneServicePerf(name: string, startTime: number, endTime: number): void {
    const duration = endTime - startTime;
    logEventAsync({
        type: "ClientPerf",
        name: name,
        data: {
            Duration: duration
        }
    });
}